import { FC } from "react";
import { useI18n } from "../../hooks/useTranslations";
import { useCarPurchasesApi } from "../../http/carPurchases/api";
import { getCarPurchaseStates, getPurchaseTypes } from "../../http/carPurchases/types";
import { formatCurrency, formatDateNumeric } from "../../types/utils";
import HistoryDialog from "../ui/HistoryDialog";
import Textarea from "../ui/Textarea";
import VersionDialog from "../ui/VersionDialog";

type CarPurchaseHistoryDialogProps = {
  itemToShowVersionsId: string;
  itemVersionToShowVersions: number;
  onClose: () => void;
};

const CarPurchaseHistoryDialog: FC<CarPurchaseHistoryDialogProps> = ({
  itemToShowVersionsId,
  itemVersionToShowVersions,
  onClose,
}) => {
  const i18n = useI18n();
  const carPurchasesApi = useCarPurchasesApi();

  const purchaseTypes = getPurchaseTypes(i18n.t);
  const states = getCarPurchaseStates(i18n.t);

  return (
    <HistoryDialog
      title={i18n.t.carOrders.versions}
      lastVersion={itemVersionToShowVersions}
      entityId={itemToShowVersionsId}
      getEntityVersionHook={carPurchasesApi.getAdminCarPurchaseVersion}
      getHistory={carPurchasesApi.getAdminCarPurchaseHistory}
      onClose={onClose}
      renderForm={(headers, dataVersion) => (
        <>
          <VersionDialog.Table headers={headers}>
            {(dataVersion.current?.subsidiary?.id ?? null) !== (dataVersion.next.subsidiary?.id ?? null) && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.subsidiaries.singular}
                  value={dataVersion.current && dataVersion.current?.subsidiary?.name}
                  edited={dataVersion.next.subsidiary?.name}
                />
              </tr>
            )}
            {dataVersion.current?.purchaseType != dataVersion.next.purchaseType && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.purchaseType}
                  value={purchaseTypes.find((x) => x.value == dataVersion.current?.purchaseType)?.label}
                  edited={purchaseTypes.find((x) => x.value == dataVersion.next.purchaseType)?.label}
                />
              </tr>
            )}
            {(dataVersion.current.supplier?.id ?? null) !== (dataVersion.next.supplier?.id ?? null) && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.suppliers.singular}
                  value={dataVersion.current?.supplier?.name}
                  edited={dataVersion.next.supplier?.name}
                />
              </tr>
            )}
            {(dataVersion.current.leasingCompany?.id ?? null) !== (dataVersion.next.leasingCompany?.id ?? null) && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.leasingCompanies.singular}
                  value={dataVersion.current?.leasingCompany?.name}
                  edited={dataVersion.next.leasingCompany?.name}
                />
              </tr>
            )}
            {(dataVersion.current.driver?.id ?? null) !== (dataVersion.next.driver?.id ?? null) && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.recipient}
                  value={dataVersion.current?.driver?.fullName}
                  edited={dataVersion.next?.driver?.fullName}
                />
              </tr>
            )}
            {(dataVersion.current.manufacturer?.id ?? null) !== (dataVersion.next.manufacturer?.id ?? null) && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.manufacturers.singular}
                  value={dataVersion.current?.manufacturer?.name}
                  edited={dataVersion.next.manufacturer?.name}
                />
              </tr>
            )}
            {dataVersion.current?.model != dataVersion.next.model && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.model}
                  value={dataVersion.current?.model}
                  edited={dataVersion.next.model}
                />
              </tr>
            )}

            {dataVersion.current?.purchaseDate != dataVersion.next.purchaseDate && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.orderDate}
                  value={dataVersion.current && formatDateNumeric(dataVersion.current?.purchaseDate!, i18n.language)}
                  edited={formatDateNumeric(dataVersion.next.purchaseDate, i18n.language)}
                />
              </tr>
            )}
            {dataVersion.current?.plannedArrivalDate != dataVersion.next.plannedArrivalDate && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.plannedArrivalDate}
                  value={
                    dataVersion.current && formatDateNumeric(dataVersion.current?.plannedArrivalDate!, i18n.language)
                  }
                  edited={formatDateNumeric(dataVersion.next.plannedArrivalDate, i18n.language)}
                />
              </tr>
            )}
            {dataVersion.current.costNet?.EuroValue != dataVersion.next.costNet?.EuroValue && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.costNet}
                  value={
                    dataVersion.current.costNet?.ForeignValue
                      ? formatCurrency(
                          dataVersion.current.costNet?.ForeignValue.Value,
                          i18n.language,
                          dataVersion.current.costNet?.ForeignValue.Currency,
                        )
                      : dataVersion.current.costNet?.EuroValue &&
                        formatCurrency(dataVersion.current.costNet?.EuroValue, i18n.language, "EUR")
                  }
                  edited={
                    dataVersion.next.costNet?.ForeignValue
                      ? formatCurrency(
                          dataVersion.next.costNet?.ForeignValue.Value,
                          i18n.language,
                          dataVersion.next.costNet?.ForeignValue.Currency,
                        )
                      : dataVersion.next.costNet?.EuroValue &&
                        formatCurrency(dataVersion.next.costNet?.EuroValue, i18n.language, "EUR")
                  }
                />
              </tr>
            )}
            {dataVersion.current.costGross?.EuroValue != dataVersion.next.costGross?.EuroValue && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.costGross}
                  value={
                    dataVersion.current.costGross?.ForeignValue
                      ? formatCurrency(
                          dataVersion.current.costGross?.ForeignValue.Value,
                          i18n.language,
                          dataVersion.current.costGross?.ForeignValue.Currency,
                        )
                      : dataVersion.current.costGross?.EuroValue &&
                        formatCurrency(dataVersion.current.costGross?.EuroValue, i18n.language, "EUR")
                  }
                  edited={
                    dataVersion.next.costGross?.ForeignValue
                      ? formatCurrency(
                          dataVersion.next.costGross?.ForeignValue.Value,
                          i18n.language,
                          dataVersion.next.costGross?.ForeignValue.Currency,
                        )
                      : dataVersion.next.costGross?.EuroValue &&
                        formatCurrency(dataVersion.next.costGross?.EuroValue, i18n.language, "EUR")
                  }
                />
              </tr>
            )}
            {dataVersion.current?.state != dataVersion.next.state && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.state}
                  value={states.find((x) => x.value == dataVersion.current?.state)?.label}
                  edited={states.find((x) => x.value == dataVersion.next.state)?.label}
                />
              </tr>
            )}

            {(!dataVersion.current || dataVersion.current?.optionals != dataVersion.next.optionals) && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.optionals}
                  value={
                    dataVersion.current && (
                      <Textarea
                        value={
                          dataVersion.current && !dataVersion.current?.optionals ? "" : dataVersion.current?.optionals
                        }
                        disabled
                      />
                    )
                  }
                  edited={<Textarea value={dataVersion.next.optionals} disabled />}
                />
              </tr>
            )}

            {dataVersion.current?.hideArrivalDateNotification != dataVersion.next.hideArrivalDateNotification && (
              <tr>
                <VersionDialog.Field
                  label={i18n.t.common.hideArrivalNotification}
                  value={dataVersion.current?.hideArrivalDateNotification}
                  edited={dataVersion.next.hideArrivalDateNotification}
                />
              </tr>
            )}
          </VersionDialog.Table>
        </>
      )}
    />
  );
};

export default CarPurchaseHistoryDialog;
